import type { Portfolio } from "@/types/Portfolio";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import { emptyNotifications } from "@/types/notifications";
import { savedViewColor } from "./savedViewHighlight";

/**
 * A combined watchlist reduced to the parts a ticker/keyword feed can honour.
 *
 * A combined watchlist is a matching rule over several things at once: other
 * watchlists, tag watchlists, and typed keywords. The equities feeds are
 * structured filings — a record has a symbol, a security name and a person's
 * name, and carries no tags — so only part of such a rule can be applied
 * there. Hiding these lists from those panels is what left a user's own
 * watchlist missing from the menu named after them, so instead the applicable
 * part is taken and the rest dropped:
 *
 *   - the terms of every watchlist it references → tickers and keywords
 *   - its own typed keywords → keywords
 *   - tag watchlists and asset-class tags → dropped; nothing here carries tags
 *
 * Returns null when nothing survives, so a tags-only list is left out of the
 * menu rather than offered as a filter that could only ever match nothing.
 */
export function combinedWatchlistAsPortfolio(
  query: CategoryQuery
): Portfolio | null {
  const referenced = query.portfolios ?? [];

  const tickers = unique(referenced.flatMap((p) => p.tickers));
  const keywords = unique([
    ...referenced.flatMap((p) => p.keywords ?? []),
    ...plainTerms(query.searchKeyword),
  ]);

  if (tickers.length === 0 && keywords.length === 0) return null;

  return {
    // Negative so it cannot collide with a portfolio id: both stores number
    // from 1, and the two end up side by side in one selection list.
    id: -(query.id ?? 0),
    name: query.queryName,
    tickers,
    keywords,
    color: savedViewColor(query),
    notifications: query.notifications ?? emptyNotifications(),
    customizations: { includeRelatedHeadlines: false },
    owner_type: query.owner_type === "NEWSQUAWK" ? "NEWSQUAWK" : "USER",
  };
}

/** True when a combined watchlist has anything a ticker/keyword feed can use. */
export const isCombinedWatchlistUsable = (query: CategoryQuery): boolean =>
  combinedWatchlistAsPortfolio(query) !== null;

/**
 * The terms in a combined watchlist's keyword box, when they are plain terms.
 *
 * That box also accepts the advanced expression syntax the headline search
 * understands. There is nowhere to send an expression here — these keywords
 * become a word-boundary match against a name field — so a query using any of
 * it is dropped whole rather than matched on its punctuation.
 */
export function plainTerms(searchKeyword: string | undefined): string[] {
  const raw = searchKeyword?.trim();
  if (!raw) return [];
  if (/[()*]|\bAND\b|\bNOT\b/.test(raw)) return [];
  return raw
    .split(/\s+OR\s+|,/)
    .map((term) => term.trim().replace(/^"(.*)"$/, "$1").trim())
    .filter(Boolean);
}

const unique = (values: string[]): string[] => Array.from(new Set(values));
